import * as RadixDialog from '@radix-ui/react-dialog'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'
import { DsButton } from '@/ds/button'
import { DsInput } from '@/ds/input'
import { useOverlayStore } from '@/ds/overlay-store'

export interface DsDialogProps {
  open: boolean
  /** Called with the outcome: true confirmed, false cancelled, null dismissed. */
  onClose: (result: boolean | null) => void
  title: string
  message?: string
  additionalMessage?: string
  tone?: DsTone
  icon?: DsIcon
  confirmLabel?: string
  cancelLabel?: string
  /** Async work runs with the confirm button in its loading state; the dialog closes when it resolves. */
  onConfirm?: () => void | Promise<void>
  canDismiss?: boolean
  destructive?: boolean
  /** Type-to-confirm for irreversible actions (compared case-insensitively). */
  requireText?: string
  requireTextHint?: string
  children?: ReactNode
}

const toneIcon: Record<DsTone, DsIcon> = { neutral: DsIcons.info, brand: DsIcons.info, success: DsIcons.success, warning: DsIcons.warning, danger: DsIcons.error, info: DsIcons.info }

export function DsDialog({ open, onClose, title, message, additionalMessage, tone = 'neutral', icon, confirmLabel, cancelLabel, onConfirm, canDismiss = true, destructive, requireText, requireTextHint, children }: DsDialogProps) {
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState('')
  const t = toneClass[destructive ? 'danger' : tone]
  const gate = requireText ? typed.trim().toLowerCase() === requireText.toLowerCase() : true

  const confirm = async () => {
    if (!gate) return
    setBusy(true)
    try {
      await onConfirm?.()
      onClose(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={(o) => !o && canDismiss && !busy && onClose(null)}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim animate-ds-fade-in data-[state=closed]:animate-ds-fade-out" />
        <RadixDialog.Content
          onEscapeKeyDown={(e) => (!canDismiss || busy) && e.preventDefault()}
          onPointerDownOutside={(e) => (!canDismiss || busy) && e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-48px)] max-w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border bg-popover p-6 text-popover-foreground shadow-lg outline-none animate-ds-scale-in data-[state=closed]:animate-ds-fade-out"
        >
          <span className={cn('mb-4 inline-flex size-11 items-center justify-center rounded-xl', t.soft, t.onSoft)}>
            <DsIconGlyph icon={icon ?? toneIcon[destructive ? 'danger' : tone]} size={22} />
          </span>
          <RadixDialog.Title className="font-display text-title2 text-foreground">{title}</RadixDialog.Title>
          {message && <RadixDialog.Description className="mt-1.5 text-body text-muted-foreground">{message}</RadixDialog.Description>}
          {additionalMessage && <p className="mt-2 text-small text-muted-foreground">{additionalMessage}</p>}
          {children && <div className="mt-4">{children}</div>}
          {requireText && (
            <div className="mt-4">
              <DsInput value={typed} onChange={setTyped} hintText={requireTextHint ?? requireText} label={`Type ${requireText} to confirm`} autoFocus autoComplete="off" />
            </div>
          )}
          {(confirmLabel || cancelLabel) && (
            <div className="mt-6 flex gap-3">
              {cancelLabel && <DsButton variant="outline" size="lg" label={cancelLabel} onClick={() => onClose(false)} disabled={busy} className="flex-1" />}
              {confirmLabel && <DsButton variant={destructive ? 'destructive' : 'primary'} size="lg" label={confirmLabel} onClick={confirm} loading={busy} disabled={!gate} className="flex-1" />}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export interface DsConfirmOptions {
  title: string
  message?: string
  confirmLabel: string
  cancelLabel: string
  destructive?: boolean
  requireText?: string
  requireTextHint?: string
  onConfirm?: () => void | Promise<void>
  tone?: DsTone
  icon?: DsIcon
}

export function confirmDialog(options: DsConfirmOptions): Promise<boolean> {
  return useOverlayStore
    .getState()
    .push((close) => <DsDialog open onClose={(r) => close(r === true)} {...options} />)
    .then((r) => r === true)
}

export interface DsNoticeOptions {
  title: string
  message?: string
  tone?: DsTone
  icon?: DsIcon
  confirmLabel?: string
  cancelLabel?: string
  canDismiss?: boolean
  onConfirm?: () => void | Promise<void>
}

/** Imperative notice: resolves true (confirm), false (cancel) or null (dismissed). */
export function showDialog(options: DsNoticeOptions): Promise<boolean | null> {
  return useOverlayStore.getState().push((close) => <DsDialog open onClose={close} {...options} />) as Promise<boolean | null>
}
